import CurrencyCode from 'components/Currency'
import Tooltips, { TooltipNameType } from 'components/Tooltips'
import messages from 'lang'
import * as React from 'react'
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl'
import { toMaxPrecision } from 'utils'

import PercentagesDrawer from './PercentagesDrawer'
import {
  Currency,
  Drawer,
  DRAWER_HIDE_DELAY,
  Error,
  InformationIcon,
  Input,
  Label,
  Wrapper
} from './TradeInput'

interface InputProps {
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onChange: (val: string) => void
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  value: string
}

interface MetaProps {
  dirty: boolean
  error?: React.ReactNode
  touched: boolean
}

type PropsType = {
  available: number
  className?: string
  currency: string
  intl: IntlShape
  maxOrder?: {
    input: {
      onChange: (val: boolean) => void
      value: boolean
    }
  }
  min: number
  onPercentageClick?: (percentage: number, newAmount: number) => void
  precision: number
  quantity: {
    input: InputProps
    meta: MetaProps
  }
  tooltip?: TooltipNameType
}

type DrawerPropsType = PropsType & {
  isFocussed: boolean
  isPercentageDisabled: (percentage: number) => boolean
  onPercentageClick: (percentage: number) => void
  value: string
}

const round = (num: number) => Math.round(num * 100) / 100

const getProbablySelectedPercetage = (max: number, value: number) => {
  if (!value || !max) return undefined
  if (value === max) return 1
  return round(value / max)
}

const getDrawerContent = ({
  available,
  isPercentageDisabled,
  onPercentageClick,
  quantity,
  value
}: DrawerPropsType) => {
  if (!quantity.meta?.touched && !quantity.meta?.error && isPercentageDisabled(1)) return null

  return (
    <>
      {!isPercentageDisabled(1) && (
        <PercentagesDrawer
          isPercentageDisabled={isPercentageDisabled}
          onPercentageClick={onPercentageClick}
          selected={getProbablySelectedPercetage(available, parseFloat(value))}
        />
      )}
      {quantity.meta?.touched && quantity.meta?.error && (
        <Error data-e2e='ticketQuantityError'>{quantity.meta.error}</Error>
      )}
    </>
  )
}

const AmountInput = (props: PropsType) => {
  const { available, className, currency, intl, maxOrder, min, precision, quantity, tooltip } =
    props
  const [isFocussed, setFocussed] = React.useState(false)
  const drawerContent = getDrawerContent({
    ...props,
    isFocussed,
    isPercentageDisabled: (percentage) => percentage * available < min,
    onPercentageClick: (percentage) => {
      quantity.input.onChange(String(toMaxPrecision(percentage * available, precision)))
      if (props.onPercentageClick) {
        props.onPercentageClick(percentage, percentage * available)
      }
    },
    value: quantity.input.value
  })

  let focusTimeout

  React.useEffect(() => {
    if (maxOrder) {
      maxOrder.input.onChange(available === Number(quantity.input.value))
    }
    return () => clearTimeout(focusTimeout)
  }, [])

  return (
    <Wrapper className={className}>
      <Label htmlFor='quantity'>
        <FormattedMessage {...messages.label_amount} />
        {tooltip ? (
          <Tooltips name={tooltip} placement='top' wrap={false}>
            <InformationIcon />
          </Tooltips>
        ) : null}
      </Label>
      <Input
        autoComplete='off'
        currency={currency}
        type='number'
        step='any'
        min={0}
        data-lpignore='true'
        data-e2e='ticketQuantity'
        {...quantity.input}
        onChange={(ev) => {
          quantity.input.onChange(ev.target.value)
        }}
        onBlur={(ev) => {
          focusTimeout = setTimeout(() => setFocussed(false), DRAWER_HIDE_DELAY)
          if (quantity.input.onBlur) quantity.input.onBlur(ev)
        }}
        onFocus={(ev) => {
          setFocussed(true)
          if (quantity.input.onFocus) quantity.input.onFocus(ev)
        }}
        placeholder={intl.formatMessage(messages.placeholder_quantity)}
        squaredBottom={!!drawerContent}
      />
      <Drawer className='amount-input-drawer' collapsed={!drawerContent}>
        {drawerContent}
      </Drawer>
      <Currency>
        <CurrencyCode code={currency} />
      </Currency>
    </Wrapper>
  )
}

export default injectIntl(AmountInput)
